'use client';

/**
 * Ethers v5 Signer Hook
 * 提供 ethers v5 signer 用于 Polymarket 交互
 *
 * 设计说明：
 * - 初始化时自动切换到 Polygon 链（Polymarket 运行在 Polygon 上）
 * - 替代原来 WalletProvider 中的 ethers5Signer 能力
 * - 其他状态（eoaAddress、authenticated）改用全局 hooks 获取
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { usePrivy, useWallets, useSignTypedData, useSignMessage } from '@privy-io/react-auth';
import { ethers as ethers5 } from 'ethers5';
import { polygon } from 'viem/chains';
import { useUserStore } from '@/app/stores/useUserStore';
import { createSilentSigner } from '@/lib/polymarket-clob';

/** Polygon 链 ID */
const POLYGON_CHAIN_ID = polygon.id;

/** Polygon 链 EIP-155 格式 */
const POLYGON_EIP155_CHAIN_ID = `eip155:${POLYGON_CHAIN_ID}`;

export interface UseEthers5SignerReturn {
  /** ethers v5 Signer 实例 */
  ethers5Signer: ethers5.Signer | null;
  /** signer 是否准备就绪 */
  isReady: boolean;
  /** 切换到 Polygon 链 */
  switchToPolygon: () => Promise<void>;
}

/**
 * 获取 ethers v5 signer
 * 用于 Polymarket CLOB 和 Relay 操作
 */
export function useEthers5Signer(): UseEthers5SignerReturn {
  const { wallets, ready: walletsReady } = useWallets();
  const { authenticated } = usePrivy();
  const { evmAddress } = useUserStore();

  const [ethers5Signer, setEthers5Signer] = useState<ethers5.Signer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 用 evmAddress 严格匹配钱包
  const wallet = useMemo(() => {
    if (!authenticated || !walletsReady || !evmAddress) {
      return null;
    }

    const matched = wallets.find((w) => w.address?.toLowerCase() === evmAddress.toLowerCase());

    // wallets 为空时属于 Privy 仍在加载，静默等待；有钱包但匹配不上才报警
    if (!matched && wallets.length > 0) {
      console.warn('[useEthers5Signer] 钱包地址不匹配', {
        evmAddress,
        availableWallets: wallets.map(w => w.address),
      });
    }

    return matched || null;
  }, [wallets, authenticated, walletsReady, evmAddress]);

  // 钱包地址（用于 useEffect 依赖，避免 wallet 对象引用变化导致重复初始化）
  const walletAddress = wallet?.address;

  // 初始化 signer（含自动切换到 Polygon 链）
  useEffect(() => {
    async function init() {
      if (!wallet || !walletsReady || !authenticated) {
        setEthers5Signer(null);
        setIsInitialized(false);
        return;
      }

      try {
        // 如果不在 Polygon 链上，尝试切换（仅在首次初始化时）
        if (wallet.chainId !== POLYGON_EIP155_CHAIN_ID) {
          try {
            await wallet.switchChain(POLYGON_CHAIN_ID);
          } catch {
            // 切换失败不阻断初始化，用户可以手动切换
          }
        }

        // 获取 provider 并创建 signer
        const provider = await wallet.getEthereumProvider();
        const ethers5Provider = new ethers5.providers.Web3Provider(provider);
        const signer = ethers5Provider.getSigner();
        setEthers5Signer(signer);
        setIsInitialized(true);
      } catch (error) {
        console.error('[useEthers5Signer] 初始化失败:', error);
        setEthers5Signer(null);
        setIsInitialized(false);
      }
    }
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, walletsReady, authenticated, evmAddress]);

  // 切换到 Polygon
  const switchToPolygon = async () => {
    if (!wallet) return;
    await wallet.switchChain(POLYGON_CHAIN_ID);
  };

  return {
    ethers5Signer,
    isReady: walletsReady && authenticated && isInitialized && !!ethers5Signer,
    switchToPolygon,
  };
}

/**
 * 获取带 Privy 静默签名能力的 signer
 * 将 ethers5Signer + Privy signTypedData/signMessage 合并为 silentSigner
 */
export function useSilentSigner(): ethers5.Signer | null {
  const { ethers5Signer } = useEthers5Signer();
  const { signTypedData } = useSignTypedData();
  const { signMessage } = useSignMessage();

  // 稳定引用 Privy 签名函数（每次渲染返回新引用，不能直接作为 useMemo 依赖）
  const signTypedDataRef = useRef(signTypedData);
  signTypedDataRef.current = signTypedData;
  const signMessageRef = useRef(signMessage);
  signMessageRef.current = signMessage;

  return useMemo(() => {
    if (!ethers5Signer) return null;
    return createSilentSigner(
      ethers5Signer,
      (...args: Parameters<typeof signTypedData>) => signTypedDataRef.current(...args),
      (...args: Parameters<typeof signMessage>) => signMessageRef.current(...args),
    );
  }, [ethers5Signer]);
}
